// import React, { useContext } from "react";
// import { ConfigContext } from "../../context/ConfigContext";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

// const Configuration = () => {
//   const { config, setConfig } = useContext(ConfigContext);

//   const handleSave = () => {
//     try {
//       localStorage.setItem("callConfig", JSON.stringify(config));

//       toast.success("Configuration saved successfully!", {
//         position: "top-right",
//         autoClose: 3000,
//       });
//     } catch (error) {
//       toast.error("Failed to save configuration. Please try again.", {
//         position: "top-right",
//         autoClose: 5000,
//       });
//     }
//   };

//   return (
//     <div className="p-6 text-gray-800">
//       <h1 className="text-2xl font-bold mb-6">Call Configuration</h1>

//       {/* First Message */}
//       <div className="mb-4">
//         <label className="block text-sm font-semibold mb-2">First Message</label>
//         <input
//           type="text"
//           value={config.firstMessage}
//           onChange={(e) =>
//             setConfig((prev) => ({ ...prev, firstMessage: e.target.value }))
//           }
//           className="w-full p-2 border rounded"
//           placeholder="Enter the initial message"
//         />
//       </div>

//       {/* Call Prompt */}
//       <div className="mb-4">
//         <label className="block text-sm font-semibold mb-2">Call Prompt</label>
//         <textarea
//           value={config.callPrompt}
//           onChange={(e) =>
//             setConfig((prev) => ({ ...prev, callPrompt: e.target.value }))
//           }
//           rows="8"
//           className="w-full p-2 border rounded"
//           placeholder="Enter the AI agent prompt text here"
//         ></textarea>
//       </div>

//       <button
//         onClick={handleSave}
//         className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
//       >
//         Save Configuration
//       </button>

//       <ToastContainer position="top-right" autoClose={3000} />
//     </div>
//   );
// };

// export default Configuration;


import React, { useContext, useEffect, useState, useCallback } from "react";
import { ConfigContext } from "../../context/ConfigContext";
import { ToastContainer, toast } from "react-toastify";
import { Plus, Trash2, Save, AlertCircle, Loader2 } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_SERVER_API


const Configuration = ({ user }) => {
  const { config, setConfig } = useContext(ConfigContext);
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newPromptName, setNewPromptName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [activeForInbound, setActiveForInbound] = useState(false);
  const [voices, setVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [selectedModelId, setSelectedModelId] = useState("eleven_turbo_v2_5");
  const [selectedAgentId, setSelectedAgentId] = useState("");

  // Load voices from backend
  const fetchVoices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/voices`);
      if (!res.ok) throw new Error("Failed to fetch voices");
      const data = await res.json();
      setVoices(data);
    } catch (err) {
      console.error("Error fetching voices:", err);
    }
  }, []);

  // Load prompts from backend
  const fetchPrompts = useCallback(async () => {
    setIsFetching(true);
    try {
      if (!user || !user.id) {
        toast.error("User information is missing. Please log in again.");
        setIsFetching(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/prompts?userId=${user.id}`);
      if (!res.ok) throw new Error("Failed to fetch prompts");
      const data = await res.json();
      setPrompts(data);
    } catch (err) {
      console.error("Error fetching prompts:", err);
      toast.error("Failed to load prompts from server.");
    } finally {
      setIsFetching(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPrompts();
    fetchVoices();
    // Do not reset config on mount if we want to keep last selected state
    // setConfig({ firstMessage: "", callPrompt: "" });
  }, [fetchPrompts, fetchVoices]);

  // Handle voice selection
  const handleVoiceChange = (e) => {
    const voiceId = e.target.value;
    setSelectedVoiceId(voiceId);

    // Auto-sync language and modelId if voice is found
    const voice = voices.find((v) => v.voiceId === voiceId);
    if (voice) {
      const language = voice.language || "en";
      const modelId = voice.modelId || "eleven_turbo_v2_5";
      const agentId = voice.agentId || "";

      setSelectedLanguage(language);
      setSelectedModelId(modelId);
      setSelectedAgentId(agentId);

      // Sync with global config
      setConfig(prev => ({
        ...prev,
        voiceId: voiceId,
        language: language,
        modelId: modelId,
        agentId: agentId
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        voiceId: "",
        language: "en",
        modelId: "eleven_turbo_v2_5",
        agentId: ""
      }));
    }
  };

  // Handle dropdown selection
  const handleSelectPrompt = (e) => {
    const name = e.target.value;
    setSelectedPrompt(name);
    setIsAddingNew(false);
    const selected = prompts.find((p) => p.name === name);
    if (selected) {
      const newConfig = {
        firstMessage: selected.firstMessage || "",
        callPrompt: selected.context || "",
        voiceId: selected.voiceId || "",
        language: selected.language || "en",
        modelId: selected.modelId || "eleven_turbo_v2_5",
        agentId: selected.agentId || "",
      };
      setConfig(newConfig);

      setActiveForInbound(selected.activeForInbound || false);
      setSelectedVoiceId(newConfig.voiceId);
      setSelectedLanguage(newConfig.language);
      setSelectedModelId(newConfig.modelId);
      setSelectedAgentId(newConfig.agentId);
    } else {
      setConfig({
        firstMessage: "",
        callPrompt: "",
        voiceId: "",
        language: "en",
        modelId: "eleven_turbo_v2_5",
        agentId: ""
      });
      setActiveForInbound(false);
      setSelectedVoiceId("");
      setSelectedLanguage("en");
      setSelectedModelId("eleven_turbo_v2_5");
      setSelectedAgentId("");
    }
  };

  const countWords = (str) => {
    if (!str) return 0;
    return str.trim().split(/\s+/).filter(word => word !== "").length;
  };

  const validatePrompt = () => {
    const charCount = config.callPrompt.length;
    if (charCount > 6000) {
      toast.error(`Context character limit exceeded! Current: ${charCount} characters (Max 6000 for stability).`, {
        icon: <AlertCircle className="text-red-500" />,
      });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validatePrompt()) return;

    if (isAddingNew) {
      await handleAddNew();
      return;
    }

    if (!selectedPrompt) {
      toast.error("Please select a context before saving.");
      return;
    }

    setIsLoading(true);
    try {
      const selected = prompts.find((p) => p.name === selectedPrompt);
      const updatedPrompt = {
        ...selected,
        firstMessage: config.firstMessage,
        context: config.callPrompt,
      };

      const res = await fetch(`${API_URL}/api/prompts/update/${selected._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...updatedPrompt,
          userId: user.id,
          activeForInbound: activeForInbound,
          voiceId: selectedVoiceId,
          language: selectedLanguage,
          modelId: selectedModelId,
        }),
      });

      if (res.ok) {
        toast.success("Context updated successfully!");
        // Update global config with latest values
        setConfig(prev => ({
          ...prev,
          voiceId: selectedVoiceId,
          language: selectedLanguage,
          modelId: selectedModelId,
          agentId: selectedAgentId
        }));
        fetchPrompts(); // Refresh the list
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.message || "Failed to update context.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Server error while saving context.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNew = async () => {
    if (!newPromptName.trim()) {
      toast.error("Please enter a name for the new context.");
      return;
    }

    setIsLoading(true);
    try {
      const newPrompt = {
        name: newPromptName,
        firstMessage: config.firstMessage,
        context: config.callPrompt,
        userId: user.id,
      };

      const res = await fetch(`${API_URL}/api/prompts/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...newPrompt,
          activeForInbound,
          voiceId: selectedVoiceId,
          language: selectedLanguage,
          modelId: selectedModelId,
          agentId: selectedAgentId,
        }),
      });

      if (res.ok) {
        toast.success("New context added successfully!");
        setIsAddingNew(false);
        setNewPromptName("");
        fetchPrompts();
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.message || "Failed to add new context.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Server error while adding context.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPrompt) return;

    if (!window.confirm(`Are you sure you want to delete "${selectedPrompt}"?`)) return;

    setIsLoading(true);
    try {
      const selected = prompts.find((p) => p.name === selectedPrompt);
      const res = await fetch(`${API_URL}/api/prompts/delete/${selected._id}?userId=${user.id}`, {
        method: "DELETE",
      });

      if (res.ok) {
        toast.success("Context deleted successfully!");
        setSelectedPrompt("");
        setConfig({ firstMessage: "", callPrompt: "" });
        fetchPrompts();
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.message || "Failed to delete context.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Server error while deleting context.");
    } finally {
      setIsLoading(false);
    }
  };

  const charCount = config.callPrompt.length;

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Context Configuration</h1>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setIsAddingNew(!isAddingNew);
                if (!isAddingNew) {
                  setSelectedPrompt("");
                  setConfig({ firstMessage: "", callPrompt: "" });
                }
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${isAddingNew
                ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                : "bg-blue-600 text-white hover:bg-blue-700 shadow-md"
                }`}
            >
              {isAddingNew ? "Cancel" : <><Plus size={18} /> New Context</>}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6">
            {/* Prompt Selection / Name Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {isAddingNew ? "New Context Name" : "Select Context"}
              </label>
              {isAddingNew ? (
                <input
                  type="text"
                  value={newPromptName}
                  onChange={(e) => setNewPromptName(e.target.value)}
                  placeholder="e.g. Real Estate Sales"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              ) : (
                <div className="flex gap-2">
                  <select
                    value={selectedPrompt}
                    onChange={handleSelectPrompt}
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition appearance-none bg-white"
                    disabled={isFetching}
                  >
                    <option value="">-- {isFetching ? "Loading..." : "Select a Context"} --</option>
                    {prompts.map((p) => (
                      <option key={p._id} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  {!isAddingNew && selectedPrompt && (
                    <button
                      onClick={handleDelete}
                      disabled={isLoading}
                      className="p-3 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition flex items-center justify-center border border-red-200"
                      title="Delete Context"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* First Message */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                First Message
              </label>
              <input
                type="text"
                value={config.firstMessage}
                onChange={(e) =>
                  setConfig((prev) => ({ ...prev, firstMessage: e.target.value }))
                }
                placeholder="Enter the initial message..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>

            {/* Voice Selection */}
            <div className="mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">AI Voice & Language</label>
                <select
                  value={selectedVoiceId}
                  onChange={handleVoiceChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition bg-white"
                >
                  <option value="">-- Use Default Agent (English) --</option>
                  {voices.map((v) => (
                    <option key={v._id} value={v.voiceId}>{v.name} ({v.language.toUpperCase()})</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 italic">Selecting a voice automatically sets the conversation language.</p>
              </div>
            </div>

            {/* Inbound Toggle */}
            <div className="mb-6 flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 text-blue-800">
                <AlertCircle size={20} className="text-blue-600" />
                <div>
                  <p className="font-semibold text-sm">Active for Inbound Calls</p>
                  <p className="text-xs opacity-80">Use this prompt for all incoming calls to your Twilio number.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveForInbound(!activeForInbound)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${activeForInbound ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${activeForInbound ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Call Prompt */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Context
                </label>
                <span className={`text-xs font-medium px-2 py-1 rounded ${charCount > 6000 ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"
                  }`}>
                  {charCount.toLocaleString()} / 6000 characters
                </span>
              </div>
              <textarea
                rows="12"
                value={config.callPrompt}
                onChange={(e) =>
                  setConfig((prev) => ({ ...prev, callPrompt: e.target.value }))
                }
                placeholder="Enter the AI agent instructions and context here..."
                className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition font-sans leading-relaxed ${charCount > 6000 ? "border-red-300 ring-red-100" : ""
                  }`}
              ></textarea>
              {charCount > 6000 && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} /> Context character limit exceeded (Max 6000 characters).
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <button
                onClick={handleSave}
                disabled={isLoading || (!selectedPrompt && !isAddingNew)}
                className="flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition shadow-md disabled:bg-gray-400 disabled:shadow-none"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Save size={20} />
                )}
                {isAddingNew ? "Create Context" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Tip: A clear and concise context helps the AI perform better.</p>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Configuration;
